/**
 * Opens Podium Webchat from product description contact links.
 *
 * Podium loads at runtime (not in page source). Console may show:
 *   bestMatchWebchatUrl = your domain  → Webchat is configured
 *   bestMatchPopupUrl = null           → Popup product not used (normal)
 *
 * Opens chat via window.PodiumWebChat.open() once the widget API is ready.
 */
(function () {
  const PODIUM_BUTTON_ID = 'podium-website-widget-button';
  const PODIUM_LAUNCHER_ID = 'podium-website-widget-launcher';
  const PODIUM_QUERY =
    `#${PODIUM_BUTTON_ID}, .ContactBubble__Bubble--text-us-cta, .ContactBubble__Bubble`;
  const TRIGGER_SELECTOR =
    '#contact_more_det, .js-podium-contact, [data-podium-contact]';
  const CONTACT_PHRASE = /please contact us for more details/i;
  const WAIT_MS = 20000;

  /* ------------------------------------------------------------------ */
  /* Podium lookup                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * @param {ParentNode} root
   * @param {string} selector
   * @returns {HTMLElement[]}
   */
  function queryShadow(root, selector) {
    /** @type {HTMLElement[]} */
    const found = [];
    try {
      root.querySelectorAll(selector).forEach((el) => {
        if (el instanceof HTMLElement) found.push(el);
      });
    } catch (e) {
      return found;
    }
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) found.push(...queryShadow(el.shadowRoot, selector));
    });
    return found;
  }

  /** @returns {HTMLElement | null} */
  function getPodiumBubble() {
    const direct = document.getElementById(PODIUM_BUTTON_ID);
    if (direct instanceof HTMLElement) return direct;
    const list = queryShadow(document, PODIUM_QUERY);
    return list.find((el) => el.id === PODIUM_BUTTON_ID) || list[0] || null;
  }

  /** @returns {boolean} */
  function isPodiumApiReady() {
    return !!(window.PodiumWebChat && typeof window.PodiumWebChat.open === 'function');
  }

  /** @returns {boolean} */
  function openPodiumViaApi() {
    if (!isPodiumApiReady()) return false;
    window.PodiumWebChat.open();
    return true;
  }

  /** @returns {Promise<boolean>} */
  function waitForPodiumReady() {
    if (isPodiumApiReady() || getPodiumBubble()) return Promise.resolve(true);

    return new Promise((resolve) => {
      let settled = false;

      const finish = (result) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        window.clearInterval(pollId);
        window.clearTimeout(timeoutId);
        resolve(result);
      };

      const ready = () => isPodiumApiReady() || !!getPodiumBubble();

      const observer = new MutationObserver(() => {
        if (ready()) finish(true);
      });

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });

      const pollId = window.setInterval(() => {
        if (ready()) finish(true);
      }, 250);

      const timeoutId = window.setTimeout(() => finish(false), WAIT_MS);
    });
  }

  /** @returns {Promise<void>} */
  function ensurePodiumLoaded() {
    if (isPodiumApiReady() || getPodiumBubble()) return Promise.resolve();
    if (typeof window.loadPodiumWidget === 'function') {
      window.loadPodiumWidget();
    }
    return waitForPodiumReady().then(() => undefined);
  }

  /* ------------------------------------------------------------------ */
  /* Trigger setup                                                       */
  /* ------------------------------------------------------------------ */

  /** @param {HTMLElement} el */
  function stripSmsTel(el) {
    if (!(el instanceof HTMLAnchorElement)) return;
    const href = el.getAttribute('href') || '';
    if (href.startsWith('sms:') || href.startsWith('tel:')) {
      el.removeAttribute('href');
    }
  }

  /** @param {HTMLElement} el */
  function styleTrigger(el) {
    el.style.cursor = 'pointer';
    el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    stripSmsTel(el);
  }

  /** @param {Element} target @returns {HTMLElement | null} */
  function getTrigger(target) {
    if (!(target instanceof Element)) return null;
    const el = target.closest(TRIGGER_SELECTOR);
    return el instanceof HTMLElement ? el : null;
  }

  /** @param {HTMLElement} desc */
  function autoEnhanceDescription(desc) {
    desc.querySelectorAll('a[href^="sms:"], a[href^="tel:"]').forEach((anchor) => {
      if (!(anchor instanceof HTMLElement)) return;
      if (!CONTACT_PHRASE.test(anchor.textContent || '')) return;
      anchor.classList.add('js-podium-contact');
      stripSmsTel(anchor);
    });

    desc.querySelectorAll('a, span, em, strong, p, button, label').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.matches(TRIGGER_SELECTOR)) return;
      if (el.closest(TRIGGER_SELECTOR)) return;
      if (el.querySelector(TRIGGER_SELECTOR)) return;
      if (el.classList.contains('product-details__podium-contact--theme')) return;

      const text = (el.textContent || '').trim();
      if (!CONTACT_PHRASE.test(text) || text.length > 80) return;

      el.classList.add('js-podium-contact');
      styleTrigger(el);
    });
  }

  function updateThemeFallbackVisibility() {
    document.querySelectorAll('.product-details__description').forEach((desc) => {
      const themeBtn = desc.querySelector('.product-details__podium-contact--theme');
      if (!(themeBtn instanceof HTMLElement)) return;

      const existing = desc.querySelector(
        '#contact_more_det, .js-podium-contact:not(.product-details__podium-contact--theme), a[href^="sms:"]'
      );

      themeBtn.hidden = !!existing;
    });
  }

  /** Assign Podium custom launcher id to a single visible trigger. */
  function wirePodiumLauncherId() {
    const existing = document.getElementById(PODIUM_LAUNCHER_ID);
    if (existing instanceof HTMLElement && !existing.hidden) return;

    document.querySelectorAll(`#${PODIUM_LAUNCHER_ID}`).forEach((el) => {
      if (el instanceof HTMLElement) el.removeAttribute('id');
    });

    const trigger =
      document.querySelector(
        '.product-details__description #contact_more_det, .product-details__description .js-podium-contact:not(.product-details__podium-contact--theme)'
      ) ||
      document.querySelector('.product-details__podium-contact--theme:not([hidden])');

    if (trigger instanceof HTMLElement) {
      trigger.id = PODIUM_LAUNCHER_ID;
    }
  }

  function scanTriggers() {
    document.querySelectorAll(TRIGGER_SELECTOR).forEach((el) => {
      if (el instanceof HTMLElement) styleTrigger(el);
    });
    document.querySelectorAll('.product-details__description').forEach(autoEnhanceDescription);
    updateThemeFallbackVisibility();
    wirePodiumLauncherId();
  }

  /** @param {HTMLElement} button */
  function drawAttentionToPodium(button) {
    button.classList.add('podium-button--highlighted');
    button.scrollIntoView({ behavior: 'smooth', block: 'end' });
    window.setTimeout(() => button.classList.remove('podium-button--highlighted'), 3000);
  }

  /** @returns {Promise<boolean>} */
  async function openPodiumChat() {
    if (window.__podiumChatOpened) return true;

    if (openPodiumViaApi()) return true;

    await ensurePodiumLoaded();

    if (openPodiumViaApi()) return true;

    const bubble = getPodiumBubble();
    if (bubble) {
      bubble.click();
      return true;
    }

    return false;
  }

  /* ------------------------------------------------------------------ */
  /* Event listeners                                                     */
  /* ------------------------------------------------------------------ */

  document.addEventListener(
    'click',
    async (event) => {
      if (event.target instanceof Element && event.target.closest(`#${PODIUM_BUTTON_ID}`)) {
        return;
      }

      const trigger = getTrigger(event.target);
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();

      styleTrigger(trigger);
      wirePodiumLauncherId();

      const opened = await openPodiumChat();
      if (!opened) {
        const bubble = getPodiumBubble();
        if (bubble) drawAttentionToPodium(bubble);
      }
    },
    true
  );

  document.addEventListener('keydown', async (event) => {
    const trigger = getTrigger(event.target);
    if (!trigger || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    styleTrigger(trigger);
    await openPodiumChat();
  });

  scanTriggers();
  new MutationObserver(scanTriggers).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('podium:ready', scanTriggers);
  window.addEventListener('podium:button-ready', scanTriggers);
  ensurePodiumLoaded().then(scanTriggers);
})();
